import type { ReactNode } from 'react';
import type { PlanStatusCardProps } from './PlanStatusCard.types';
import { PLAN_COLORS } from './planColors';

const WARNING = { icon: '#B45309', iconBg: '#FDF0DC', barTrack: '#F6E7CD', barFill: '#B45309', sub: '#8C6A2F' };
const EXPIRED = { icon: '#B42318', iconBg: '#FDE7E4', iconBgSolid: '#B42318', sub: '#8C3B34' };

/* ---------- piezas reutilizables ---------- */

const Card = ({ gap = 'gap-2', children }: { gap?: string; children: ReactNode }) => (
  <div className={`bg-white rounded-[13px] p-3 flex flex-col ${gap}`}>{children}</div>
);

const Label = ({ color, children }: { color: string; children: ReactNode }) => (
  <span className="text-[9.5px] tracking-[.14em] uppercase font-bold" style={{ color }}>
    {children}
  </span>
);

/** Etiqueta con símbolo de alerta a la izquierda */
const AlertLabel = ({
  color,
  bg,
  iconColor = color,
  children,
}: {
  color: string;
  bg: string;
  iconColor?: string;
  children: ReactNode;
}) => (
  <div className="flex items-center gap-1.5">
    <span
      className="w-4 h-4 rounded-[5px] font-display text-[10px] font-extrabold flex items-center justify-center shrink-0"
      style={{ background: bg, color: iconColor }}
    >
      !
    </span>
    <Label color={color}>{children}</Label>
  </div>
);

/**
 * Fila con el dato principal a la izquierda y el CTA alineado a su derecha.
 * Vive en su PROPIA fila (separada de Label/AlertLabel de arriba) para que el
 * texto largo de "Prueba gratis · {planNombre}" nunca tenga que competir por
 * espacio con el botón y se parta en dos líneas.
 */
const MainRow = ({
  cta,
  ctaColor,
  ctaBold = false,
  onAccion,
  children,
}: {
  cta: string;
  ctaColor: string;
  ctaBold?: boolean;
  onAccion?: () => void;
  children: ReactNode;
}) => (
  <div className="flex items-baseline justify-between gap-2.5">
    {children}
    <button
      type="button"
      onClick={onAccion}
      className={`text-[11.5px] whitespace-nowrap ${ctaBold ? 'font-bold' : 'font-semibold'}`}
      style={{ color: ctaColor }}
    >
      {cta} ›
    </button>
  </div>
);

const Counter = ({ dias, sufijo }: { dias?: number; sufijo: string }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="font-display text-[22px] leading-none text-[#12263F] font-extrabold tracking-[-0.03em]">
      {dias}
    </span>
    <span className="font-display text-[12px] text-[#3F5162] font-semibold">{sufijo}</span>
  </div>
);

const Bar = ({ progreso, track, fill }: { progreso: number; track: string; fill: string }) => (
  <div className="h-[5px] rounded-full overflow-hidden" style={{ background: track }}>
    <div className="h-full rounded-full" style={{ width: `${progreso}%`, background: fill }} />
  </div>
);

const Sub = ({ color, children }: { color: string; children: ReactNode }) => (
  <span className="text-[11.5px] leading-[1.4]" style={{ color }}>
    {children}
  </span>
);

const Title = ({ children }: { children: ReactNode }) => (
  <span className="font-display text-[15px] text-[#12263F] font-bold">{children}</span>
);

const PlanName = ({ children }: { children: ReactNode }) => (
  <span className="font-display text-[16px] text-[#12263F] font-bold tracking-[-0.02em]">{children}</span>
);

/* ---------- componente ---------- */

export const PlanStatusCard = (props: PlanStatusCardProps) => {
  const { plan, planNombre, estado, progreso = 0, fechaVencimiento, fechaRenovacion, etiquetaAccion, onAccion } = props;
  const colors = PLAN_COLORS[plan];

  /* --- Demos --- */
  if (estado === 'demoPublica' || estado === 'demoPrivada') {
    const esPublica = estado === 'demoPublica';
    return (
      <Card>
        <div className="flex flex-col gap-1">
          <Label color="#475569">{esPublica ? 'Estás explorando' : 'Estás viendo un ejemplo'}</Label>
          <PlanName>{planNombre}</PlanName>
          <Sub color="#6B7280">
            {esPublica ? 'Nada de lo que hagas aquí se guarda' : 'Empresa de práctica, sin plan'}
          </Sub>
        </div>
        <button
          type="button"
          onClick={onAccion}
          className="mt-0.5 bg-[#334155] text-white rounded-[9px] px-3 py-2.5 text-[12.5px] font-semibold"
        >
          {etiquetaAccion ?? (esPublica ? 'Crear mi cuenta gratis' : 'Crear mi empresa')} ›
        </button>
      </Card>
    );
  }

  /* --- Plan pagado vigente --- */
  if (estado === 'pagado') {
    const { facturacion, renovacionAutomatica = true } = props;
    return (
      <Card gap="gap-1">
        <span
          className="inline-flex self-start items-center gap-1.5 text-[9.5px] tracking-[.14em] uppercase font-bold"
          style={{ color: colors.accent }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: colors.accent }} />
          Plan activo · {facturacion}
        </span>
        <MainRow cta={etiquetaAccion ?? 'Administrar plan'} ctaColor={colors.accent} onAccion={onAccion}>
          <PlanName>{planNombre}</PlanName>
        </MainRow>
        {fechaRenovacion && (
          <Sub color="#5B6B7C">
            {renovacionAutomatica
              ? `Renovación automática el ${fechaRenovacion}`
              : `Activo hasta el ${fechaRenovacion}`}
          </Sub>
        )}
      </Card>
    );
  }

  /* --- Pago en validación --- */
  if (estado === 'enValidacion') {
    return (
      <Card gap="gap-1">
        <span
          className="inline-flex self-start items-center gap-1.5 text-[9.5px] tracking-[.14em] uppercase font-bold"
          style={{ color: WARNING.icon }}
        >
          <span className="w-1.5 h-1.5 rounded-full" style={{ background: WARNING.icon }} />
          En validación
        </span>
        <MainRow cta={etiquetaAccion ?? 'Ver estado'} ctaColor={WARNING.icon} onAccion={onAccion}>
          <PlanName>{planNombre}</PlanName>
        </MainRow>
        <Sub color={WARNING.sub}>Estamos confirmando tu pago</Sub>
      </Card>
    );
  }

  /* --- Cancelación programada --- */
  if (estado === 'cancelacionProgramada') {
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={WARNING.icon} bg={WARNING.iconBg}>
          Cancelación programada · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Reactivar'} ctaColor={WARNING.icon} ctaBold onAccion={onAccion}>
          <Title>Tu plan sigue activo</Title>
        </MainRow>
        {fechaRenovacion && <Sub color={WARNING.sub}>Se cancelará el {fechaRenovacion}</Sub>}
      </Card>
    );
  }

  /* --- Solo consulta --- */
  if (estado === 'soloConsulta') {
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={WARNING.icon} bg={WARNING.iconBg}>
          Solo consulta · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Actualizar plan'} ctaColor={WARNING.icon} ctaBold onAccion={onAccion}>
          <Title>Acceso limitado a consulta</Title>
        </MainRow>
        <Sub color={WARNING.sub}>Actualiza tu plan para volver a emitir</Sub>
      </Card>
    );
  }

  /* --- Cancelado --- */
  if (estado === 'cancelado') {
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={EXPIRED.icon} bg={EXPIRED.iconBgSolid} iconColor="#fff">
          Plan cancelado · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Elegir plan'} ctaColor={EXPIRED.icon} ctaBold onAccion={onAccion}>
          <Title>Elige un plan para continuar</Title>
        </MainRow>
        {fechaRenovacion && <Sub color={EXPIRED.sub}>Cancelado el {fechaRenovacion}</Sub>}
      </Card>
    );
  }

  /* --- Eliminado --- */
  if (estado === 'eliminado') {
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={EXPIRED.icon} bg={EXPIRED.iconBgSolid} iconColor="#fff">
          Plan eliminado · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Contactar soporte'} ctaColor={EXPIRED.icon} ctaBold onAccion={onAccion}>
          <Title>Este plan ya no está disponible</Title>
        </MainRow>
      </Card>
    );
  }

  /* --- Plan pagado próximo a vencer --- */
  if (estado === 'pagadoPorVencer') {
    const { diasRestantes, renovacionAutomatica = true } = props;
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={WARNING.icon} bg={WARNING.iconBg}>
          {renovacionAutomatica ? 'Vence pronto' : 'Termina pronto'} · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Renovar'} ctaColor={WARNING.icon} ctaBold onAccion={onAccion}>
          <Counter dias={diasRestantes} sufijo={fechaVencimiento ? `días · hasta ${fechaVencimiento}` : 'días restantes'} />
        </MainRow>
        <Sub color={WARNING.sub}>
          {renovacionAutomatica
            ? 'La renovación automática está desactivada'
            : 'Tu plan no se renueva solo'}
        </Sub>
      </Card>
    );
  }

  /* --- Plan pagado vencido --- */
  if (estado === 'pagadoVencido') {
    const { renovacionAutomatica = true } = props;
    return (
      <Card gap="gap-1.5">
        <AlertLabel color={EXPIRED.icon} bg={EXPIRED.iconBgSolid} iconColor="#fff">
          {renovacionAutomatica ? 'Plan vencido' : 'Plan terminado'} · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Renovar'} ctaColor={EXPIRED.icon} ctaBold onAccion={onAccion}>
          <Title>Renueva para seguir</Title>
        </MainRow>
        {fechaRenovacion && (
          <Sub color={EXPIRED.sub}>
            {renovacionAutomatica ? `Venció el ${fechaRenovacion}` : `Terminó el ${fechaRenovacion}`}
          </Sub>
        )}
      </Card>
    );
  }

  /* --- Prueba terminada --- */
  if (estado === 'vencido') {
    const { diasRestantes } = props;
    const esUltimoDia = diasRestantes > 0;
    return (
      <Card gap="gap-1.5">
        <AlertLabel
          color={EXPIRED.icon}
          bg={esUltimoDia ? EXPIRED.iconBg : EXPIRED.iconBgSolid}
          iconColor={esUltimoDia ? EXPIRED.icon : '#fff'}
        >
          {esUltimoDia ? 'Último día' : 'Prueba terminada'} · {planNombre}
        </AlertLabel>
        <MainRow cta={etiquetaAccion ?? 'Comprar plan'} ctaColor={EXPIRED.icon} ctaBold onAccion={onAccion}>
          <Title>{esUltimoDia ? 'Hoy vence tu prueba' : 'Elige tu plan y sigue'}</Title>
        </MainRow>
        <Sub color={EXPIRED.sub}>
          {esUltimoDia ? 'Elige un plan y sigue facturando' : 'Guardamos tus datos 30 días'}
        </Sub>
      </Card>
    );
  }

  /* --- Prueba gratis: en curso y últimos días --- */
  const { diasRestantes } = props;
  const esUltimosDias = estado === 'ultimosDias';

  return (
    <Card>
      {esUltimosDias ? (
        <AlertLabel color={WARNING.icon} bg={WARNING.iconBg}>
          Últimos días · {planNombre}
        </AlertLabel>
      ) : (
        <Label color={colors.accent}>Prueba gratis · {planNombre}</Label>
      )}

      <MainRow
        cta={etiquetaAccion ?? 'Ver planes'}
        ctaColor={esUltimosDias ? WARNING.icon : colors.accent}
        ctaBold={esUltimosDias}
        onAccion={onAccion}
      >
        <Counter
          dias={diasRestantes}
          sufijo={esUltimosDias && fechaVencimiento ? `días · vence ${fechaVencimiento}` : 'días restantes'}
        />
      </MainRow>
      
      <Bar
        progreso={progreso}
        track={esUltimosDias ? WARNING.barTrack : colors.barTrack}
        fill={esUltimosDias ? WARNING.barFill : colors.barFill}
      />
    </Card>
  );
};
